import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const PREVIEW_MS = 2800;

/**
 * When Lithuanian TTS runs, playTTS dispatches `ttt-lithuanian-tts-preview` with the
 * shaped (syllable-spaced) string so users briefly see what will be spoken, same idea
 * as nonverbal’s polished line, but instant local shaping. Mount once under LanguageProvider.
 */
export function TtsLithuanianPreviewBridge() {
  const { t } = useLanguage();
  const [line, setLine] = useState(null);

  const hide = useCallback(() => setLine(null), []);

  useEffect(() => {
    let timer;
    const onPreview = (e) => {
      const text = e?.detail?.text;
      if (typeof text !== 'string' || !text.trim()) return;
      clearTimeout(timer);
      setLine(text.trim().slice(0, 400));
      timer = setTimeout(hide, PREVIEW_MS);
    };
    window.addEventListener('ttt-lithuanian-tts-preview', onPreview);
    return () => {
      window.removeEventListener('ttt-lithuanian-tts-preview', onPreview);
      clearTimeout(timer);
    };
  }, [hide]);

  if (!line) return null;

  const title = t('tts.ltPreviewTitle');

  return createPortal(
    <div
      className="pointer-events-none fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-[9999] w-[min(100vw-2rem,28rem)] -translate-x-1/2 rounded-2xl border border-white/20 bg-[#1a1a2e]/95 px-4 py-3 text-center shadow-lg backdrop-blur-md"
      role="status"
      aria-live="polite"
    >
      <p className="text-xs font-medium uppercase tracking-wide text-[#9DDBB9]/90">{title}</p>
      <p className="mt-1 text-base leading-snug text-white">{line}</p>
    </div>,
    document.body
  );
}
